/**
 * TastingCard.js — Tasting Experience Card (cardType "tasting").
 *
 * Matches the original Fogarty design: cover image, serif/accent title, the
 * FULL description (no clamp), an outlined "Learn more" button, and a footer
 * split between price + price unit (left) and a solid BOOK-NOW CTA (right).
 *
 * BE FIELDS (apps/resources/feature_cards/constants.py — tasting_experience):
 *   title, description, price, price_unit, available, cta_text, cta_link,
 *   cover_image_link, message (LLM-built "Tell me more about ..." line used by
 *   the Learn-more follow-up; see apps/agent/pipeline/feature_cards.py).
 *   (boolean fields may arrive as real bools or "true"/"false" strings.)
 *
 * LEARN MORE:
 *   Mirrors old cardClicked -> sendMessage: emits ws-prompt-select with the
 *   card's `message`, which chat.js echoes as a user turn and sends to the BE
 *   for the detailed view. No unique_id channel needed.
 *
 * REFERENCE (old code):
 *   thomasfogarty/script.js createWineTastingCard() (1991-2050)
 */

import { getDefaultCardLogo } from "../../core/defaultCardLogos.js";
import { emit, isChatDisabled } from "../../core/state.js";

// Old Fogarty "Learn more" icon (script.js createWineTastingCard, ~2017):
// a share/scatter-nodes glyph. Kept verbatim for parity; stroke uses
// currentColor so it follows the button's accent color.
const LEARN_MORE_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M14.6665 3.3335V1.3335L10.7398 5.26016M13.0666 10.5934C13.0666 11.698 12.1712 12.5934 11.0666 12.5934C9.96208 12.5934 9.06665 11.698 9.06665 10.5934C9.06665 9.48885 9.96208 8.59342 11.0666 8.59342C12.1712 8.59342 13.0666 9.48885 13.0666 10.5934ZM7.40649 4.93343C7.40649 6.038 6.51106 6.93343 5.40649 6.93343C4.30192 6.93343 3.40649 6.038 3.40649 4.93343C3.40649 3.82886 4.30192 2.93343 5.40649 2.93343C6.51106 2.93343 7.40649 3.82886 7.40649 4.93343ZM10.2333 7.76676C10.2333 8.87133 9.33789 9.76676 8.23332 9.76676C7.12875 9.76676 6.23332 8.87133 6.23332 7.76676C6.23332 6.66219 7.12875 5.76676 8.23332 5.76676C9.33789 5.76676 10.2333 6.66219 10.2333 7.76676ZM11.2734 3.90023C11.2734 5.0048 10.3779 5.90023 9.27336 5.90023C8.16879 5.90023 7.27336 5.0048 7.27336 3.90023C7.27336 2.79566 8.16879 1.90023 9.27336 1.90023C10.3779 1.90023 11.2734 2.79566 11.2734 3.90023ZM14.0999 6.72689C14.0999 7.83146 13.2044 8.72689 12.0999 8.72689C10.9953 8.72689 10.0999 7.83146 10.0999 6.72689C10.0999 5.62232 10.9953 4.72689 12.0999 4.72689C13.2044 4.72689 14.0999 5.62232 14.0999 6.72689ZM6.37329 8.80013C6.37329 9.9047 5.47786 10.8001 4.37329 10.8001C3.26872 10.8001 2.37329 9.9047 2.37329 8.80013C2.37329 7.69556 3.26872 6.80013 4.37329 6.80013C5.47786 6.80013 6.37329 7.69556 6.37329 8.80013ZM9.19979 11.6268C9.19979 12.7314 8.30436 13.6268 7.19979 13.6268C6.09522 13.6268 5.19979 12.7314 5.19979 11.6268C5.19979 10.5222 6.09522 9.62679 7.19979 9.62679C8.30436 9.62679 9.19979 10.5222 9.19979 11.6268ZM5.33325 12.6668C5.33325 13.7714 4.43782 14.6668 3.33325 14.6668C2.22868 14.6668 1.33325 13.7714 1.33325 12.6668C1.33325 11.5623 2.22868 10.6668 3.33325 10.6668C4.43782 10.6668 5.33325 11.5623 5.33325 12.6668Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function isTrue(v) {
  return v === true || v === "true";
}

export default class TastingCard {
  /**
   * @param {Object} exp - tasting_experience feature card
   * @returns {HTMLElement}
   */
  render(exp) {
    const card = document.createElement("div");
    card.className = "ws-card ws-tasting-card";

    const unavailable = exp.available !== undefined && !isTrue(exp.available);

    // ⚠ SHARED/BASE — all tenants using the base tasting card.
    // Parity: cover image AND Learn more both open the detailed view (old
    // cardClicked -> sendMessage with the card's `message` AND its unique_id).
    // uniqueId makes the BE return this card's detail view directly.
    const openDetail = () => {
      if(isChatDisabled()) return;
      const label = exp.message || `Tell me more about ${exp.title || "this tasting"}`;
      emit("ws-prompt-select", { label, value: label, uniqueId: exp.unique_id });
    };

    const head = document.createElement("div");
    head.className = "ws-card-head";
    const img = document.createElement("img");
    img.className = "ws-card-img";
    img.src = exp.cover_image_link || getDefaultCardLogo();
    img.alt = exp.title || "Tasting";
    img.loading = "lazy";
    img.style.cursor = "pointer";
    img.addEventListener("click", openDetail);
    head.appendChild(img);

    card.appendChild(head);

    const body = document.createElement("div");
    body.className = "ws-card-body";

    if (exp.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title";
      title.title = exp.title;
      title.textContent = exp.title;
      body.appendChild(title);
    }

    // Full description — the original shows it in full, not truncated.
    if (exp.description) {
      const desc = document.createElement("p");
      desc.className = "ws-card-desc-full";
      desc.textContent = exp.description;
      body.appendChild(desc);
    }

    // Learn more → drives a follow-up turn (detailed view) via the chat flow.
    const learn = document.createElement("button");
    learn.type = "button";
    learn.className = "ws-card-learnmore";
    learn.innerHTML = `<span class="ws-learnmore-icon">${LEARN_MORE_ICON}</span><span>Learn more</span>`;
    learn.addEventListener("click", openDetail);
    body.appendChild(learn);

    // Footer: price + unit on the left, BOOK NOW on the right.
    const footer = document.createElement("div");
    footer.className = "ws-tasting-footer";

    const priceBlock = document.createElement("div");
    priceBlock.className = "ws-price-block";
    // Design (matches live): the amount ("$50") sits on top with the unit
    // ("Per Person") stacked beneath it. The BE ideally provides `price` and
    // `price_unit` separately. Some feature cards instead bake the unit into
    // the price field ("$30 per person") with an empty price_unit — split that
    // here so it still stacks like the split-field cards (Title-case the
    // recovered unit to match the "Per Person" convention).
    let priceText = exp.price;
    let unitText = exp.price_unit;
    if (priceText && !unitText) {
      const m = String(priceText).match(/^\s*(.+?)\s+(per\s+.+)$/i);
      if (m) {
        priceText = m[1];
        unitText = m[2].replace(/\b\w/g, (c) => c.toUpperCase());
      }
    }
    if (priceText) {
      const price = document.createElement("p");
      price.className = "ws-card-price";
      price.textContent = priceText;
      priceBlock.appendChild(price);
    }
    if (unitText) {
      const unit = document.createElement("p");
      unit.className = "ws-card-unit";
      unit.textContent = unitText;
      priceBlock.appendChild(unit);
    }
    footer.appendChild(priceBlock);

    // ⚠ SHARED/BASE — this card renders for EVERY tenant using the base tasting
    // card (Fogarty, Valle, …), not one winery. Change with care.
    // Parity: the old createWineTastingCard rendered the CTA (RESERVE NOW / BOOK
    // NOW) unconditionally whenever the card had cta_text — the <a> was emitted
    // even before a cta_link was filled in. So gate on cta_text, not cta_link:
    // the button always shows, and cta_link (set in the Feature Cards config) is
    // the destination when present.
    // ⚠ SHARED/BASE — exact old parity. The old createWineTastingCard (script.js
    // 2926) renders the CTA UNCONDITIONALLY as <a href="${cta_link}"
    // target="_blank">${cta_text}</a> — always setting href even when cta_link is
    // empty. Verified against the live old backend: Valle's Tasting Room card has
    // cta_text "Reserve Now" but an EMPTY cta_link, so the old button opens the
    // current page in a new tab. We replicate that exactly: always set href +
    // target. Set a real cta_link in the Feature Cards config to point it at a
    // reservation page.
    const ctaText = exp.cta_text || (exp.cta_link ? "Book Now" : "");
    if (ctaText && !unavailable) {
      const cta = document.createElement("a");
      cta.className = "ws-card-cta";
      cta.href = exp.cta_link || "";
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
      cta.textContent = ctaText;
      footer.appendChild(cta);
    }

    body.appendChild(footer);
    card.appendChild(body);
    return card;
  }
}
